import React, {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useState,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';

import uniq from 'lodash/uniq';
import { Error, ErrorContainer } from 'components/theme/Form/Form';
import ToggleFeature from 'components/ToggleFeature';

import { useDispatch, useBrandId } from 'AppProvider';
import { deleteDiscountCode, validatePromoCode } from 'actions/discount-code';

import {
  ApplyDiscountButton,
  DiscountInput,
  DiscountInputContainer,
  DiscountSection,
} from './styles';

type CartItem = {
  experienceId: string;
  sessionDate?: string;
};

const DiscountForm = ({
  cartExperienceIds,
  cartItems,
  findEligible,
  validateDiscountCodeError,
  validateDiscountCodeStatus,
  validateCreditCodesError,
  validateCreditCodeStatus,
}: {
  cartExperienceIds: string[];
  cartItems?: CartItem[];
  findEligible?: boolean;
  validateDiscountCodeError: string;
  validateDiscountCodeStatus: string;
  validateCreditCodesError: string;
  validateCreditCodeStatus: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const brandId = useBrandId();
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (validateDiscountCodeError) {
      setCode('');
    }
  }, [validateDiscountCodeError]);

  useEffect(() => {
    if (validateDiscountCodeError || validateCreditCodesError) {
      inputRef.current?.focus();
    }
  }, [validateDiscountCodeError, validateCreditCodesError]);

  useEffect(() => {
    return function cleanup() {
      dispatch(deleteDiscountCode());
    };
  }, []);

  const validate = () => {
    if (!code) return;
    const payload: {
      experienceIds: string[];
      items?: Array<{ experienceId: string; sessionDate: string }>;
    } = {
      experienceIds: uniq(cartExperienceIds),
    };
    if (cartItems && cartItems.length > 0) {
      const validItems = cartItems.filter(
        (item): item is { experienceId: string; sessionDate: string } =>
          !!item.sessionDate,
      );
      if (validItems.length > 0) {
        payload.items = validItems;
      }
    }
    dispatch(validatePromoCode(code, brandId, payload));
    setCode('');
  };

  return (
    <ToggleFeature featureId="discountCode">
      <div>
        <DiscountSection className="cart__discount-section">
          <DiscountInputContainer>
            <DiscountInput
              ref={inputRef}
              type="text"
              name="discountCode"
              placeholder={findEligible ? t('promoOrFind') : t('promoCode')}
              value={code}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setCode(e.target.value);
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  validate();
                }
              }}
              aria-label={t('enterPromoCode')}
              aria-invalid={
                !!validateDiscountCodeError || !!validateCreditCodesError
              }
              aria-describedby={
                validateDiscountCodeError || validateCreditCodesError
                  ? 'discountCode-error'
                  : undefined
              }
            />
            <ApplyDiscountButton
              className="cart__discount-button"
              type="button"
              disabled={
                validateDiscountCodeStatus === 'pending' ||
                validateCreditCodeStatus === 'pending'
              }
              onClick={validate}
              aria-label={t('applyPromoCode')}
            >
              {t('apply')}
            </ApplyDiscountButton>
          </DiscountInputContainer>
          {(validateCreditCodesError || validateDiscountCodeError) && (
            <ErrorContainer id="discountCode-error">
              <Error>
                {validateDiscountCodeError || validateCreditCodesError}
              </Error>
            </ErrorContainer>
          )}
        </DiscountSection>
      </div>
    </ToggleFeature>
  );
};

export default DiscountForm;
